import { itemUsesColor } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, type ItemDetailData, type VariantRow } from '../api';
import { useAuth, canWrite } from '../auth';
import { ColourPicker } from '../components/ColourPicker';
import { MarketplaceCompare } from '../components/MarketplaceCompare';
import { ColorSwatch, ItemImage } from '../components/ItemImage';
import { DetailsTab, HistoryTab, InventoryTab, NotesTab, PriceHistoryTab, ProfitabilityTab, TransactionsTab } from '../components/itemTabs';
import { PartsListTab } from '../components/PartsListTab';
import { VendorsTab } from '../components/VendorsTab';
import { TYPE_LABEL } from '../lib/labels';
import { useCategories, useLocations } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, PageHeader, Select, Spinner, Stat, cn, money, number, plural } from '../ui';

const TABS = [
  ['details', 'Overview'],
  ['colours', 'Colours'],
  ['inventory', 'Inventory'],
  ['parts', 'Parts list'],
  ['vendors', 'Suppliers'],
  ['incoming', 'Incoming'],
  ['profit', 'Profitability'],
  ['transactions', 'Transactions'],
  ['history', 'Stock history'],
  ['price-history', 'Price history'],
  ['notes', 'System notes'],
] as const;
type Tab = (typeof TABS)[number][0];

export function ItemDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some(([k]) => k === params.get('tab')) ? (params.get('tab') as Tab) : 'details';
  const qc = useQueryClient();
  const { user } = useAuth();
  const locations = useLocations();
  const [editing, setEditing] = useState(false);
  const [pictureColor, setPictureColor] = useState<number | null>(null);
  const [picked, setPicked] = useState<number[]>([]);

  const detail = useQuery({ queryKey: ['item', id], queryFn: () => api<ItemDetailData>(`/api/v1/items/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['item', id] });
    void qc.invalidateQueries({ queryKey: ['items'] });
    for (const k of ['item-inventory', 'item-transactions', 'item-movements', 'item-market', 'item-marketplaces', 'item-price-history', 'item-notes']) void qc.invalidateQueries({ queryKey: [k] });
  };

  const addColours = useMutation({
    mutationFn: () => api(`/api/v1/items/${id}/variants`, { body: { colorIds: picked } }),
    onSuccess: () => {
      setPicked([]);
      refresh();
    },
  });
  const patchVariant = useMutation({
    mutationFn: (v: { id: number; patch: Record<string, unknown> }) => api(`/api/v1/variants/${v.id}`, { method: 'PATCH', body: v.patch }),
    onSuccess: refresh,
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error || !detail.data) return <ErrorMessage error={detail.error} />;
  const { item, variants, incoming, totals } = detail.data;
  const hasColours = itemUsesColor(item.itemType);
  // The picture is in the colour with the most stock and orders unless one is chosen. Only parts differ by colour.
  const bestColor = [...variants].filter((v) => v.active).sort((a, b) => b.onHand + b.onOrder - (a.onHand + a.onOrder))[0]?.colorId ?? 0;
  const shownColor = pictureColor ?? bestColor;

  return (
    <>
      <PageHeader
        title={`${item.itemNo} ${item.name}`}
        subtitle={
          <>
            <Link to="/items" className="hover:underline">
              Items
            </Link>{' '}
            / {TYPE_LABEL[item.itemType]}
            {item.categoryName ? ` · ${item.categoryName}` : ''}
            {item.yearReleased ? ` · ${item.yearReleased}` : ''}
            {item.weightG ? ` · ${Number(item.weightG)} g` : ''}
          </>
        }
        actions={
          <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
        }
      />
      <div className="space-y-4">
        {editing && <EditItem item={item} onDone={() => { setEditing(false); refresh(); }} />}

        <Card>
          <div className="flex flex-wrap items-center gap-6">
            <ItemImage itemType={item.itemType} itemNo={item.itemNo} colorId={shownColor} className="size-44 bg-white sm:size-52" />
            <div className="min-w-60 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-semibold">{item.itemNo}</span>
                {!item.detailsFetchedAt && <Badge tone="amber">basic record</Badge>}
                {item.isObsolete && <Badge tone="amber">obsolete</Badge>}
              </div>
              <div className="text-slate-700">{item.name}</div>
              <div className="text-sm text-slate-500">
                {[item.categoryName, item.yearReleased, item.weightG ? `${Number(item.weightG)} g` : null].filter(Boolean).join(' · ')}
              </div>
              {hasColours && variants.length > 1 && (
                <div>
                  <div className="mb-1 text-xs text-slate-500">Show it in</div>
                  <div className="flex flex-wrap gap-1.5" role="group" aria-label="Picture colour">
                    {variants.filter((v) => v.active).slice(0, 24).map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        title={v.colorName}
                        aria-label={`Show in ${v.colorName}`}
                        aria-pressed={shownColor === v.colorId}
                        onClick={() => setPictureColor(v.colorId)}
                        className={cn('size-6 rounded-full ring-1 ring-slate-300', shownColor === v.colorId && 'ring-2 ring-slate-900')}
                        style={{ background: v.colorRgb ? `#${v.colorRgb}` : '#e2e8f0' }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label={hasColours ? 'Colours' : 'Children'} value={number(totals.variants)} note="In use" />
          <Stat label="On hand" value={number(totals.onHand)} note="In stock now" />
          <Stat label="On order" value={number(totals.onOrder)} note={incoming.length ? plural(new Set(incoming.map((i) => i.purchaseId)).size, 'order') : 'Nothing coming'} />
          <Stat
            label="Stock value"
            value={money(totals.stockValue)}
            note="At cost: each piece at what it was bought for (free stock counts as nil)"
          />
          <Stat label="Expected" value={number(totals.onHand + totals.onOrder)} note="On hand plus on order" />
        </div>

        <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              role="tab"
              type="button"
              aria-selected={tab === k}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('tab', k);
                setParams(next, { replace: true });
              }}
              className={cn('-mb-px border-b-2 px-4 py-2 text-sm font-medium', tab === k ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800')}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'details' && (
          <div className="space-y-4">
            <MarketplaceCompare
              itemId={item.id}
              canRefresh={canWrite(user)}
              inUse={new Set(variants.filter((v) => v.onHand > 0 || v.onOrder > 0 || v.lots > 0).map((v) => v.colorId))}
            />
            <DetailsTab item={item} onChanged={refresh} />
          </div>
        )}
        {tab === 'inventory' && <InventoryTab itemId={item.id} />}
        {tab === 'vendors' && <VendorsTab itemId={item.id} />}
        {tab === 'parts' && <PartsListTab itemId={item.id} itemType={item.itemType} />}
        {tab === 'incoming' && <IncomingTab incoming={incoming} />}
        {tab === 'profit' && <ProfitabilityTab itemId={item.id} />}
        {tab === 'transactions' && <TransactionsTab itemId={item.id} />}
        {tab === 'history' && <HistoryTab itemId={item.id} />}
        {tab === 'price-history' && <PriceHistoryTab itemId={item.id} />}
        {tab === 'notes' && <NotesTab itemId={item.id} />}
        {tab === 'colours' && (
          <>
        <Card title={hasColours ? 'Colours' : 'Stock'}>
              {variants.length === 0 ? (
                <p className="text-sm text-slate-500">No colours yet. Tick the colours you keep this in below, and each becomes a child you can stock and order.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data w-full min-w-[62rem] text-sm">
                    <thead>
                      <tr>
                        <th>{hasColours ? 'Colour' : 'Child'}</th>
                        <th className="border-l border-slate-100 text-right" title="New condition: in stock now">New in stock</th>
                        <th className="text-right" title="New condition: ordered, not yet received">New on order</th>
                        <th className="border-l border-slate-100 text-right" title="Used condition: in stock now">Used in stock</th>
                        <th className="text-right" title="Used condition: ordered, not yet received">Used on order</th>
                        <th className="border-l border-slate-100 text-right" title="New and used together">Total in stock</th>
                        <th className="text-right" title="New and used together">Total on order</th>
                        <th className="border-l border-slate-100" title="Where new is usually kept. New and used never share a bin.">New bin</th>
                        <th title="Where used is usually kept. New and used never share a bin.">Used bin</th>
                        <th>Notes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((v) => (
                        <VariantLine
                          key={`${v.id}-${v.notes ?? ''}-${v.defaultLocationNewId ?? ''}-${v.defaultLocationUsedId ?? ''}-${v.active}`}
                          v={v}
                          itemNo={item.itemNo}
                          locations={locations.data ?? []}
                          busy={patchVariant.isPending}
                          onPatch={(patch) => patchVariant.mutate({ id: v.id, patch })}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3">
                <ErrorMessage error={patchVariant.error} />
              </div>
            </Card>

            {hasColours && (
              <Card
                title="Add colours"
                actions={
                  <Button disabled={picked.length === 0 || addColours.isPending} onClick={() => addColours.mutate()}>
                    {addColours.isPending ? 'Adding…' : picked.length === 0 ? 'Add colours' : `Add ${plural(picked.length, 'colour')}`}
                  </Button>
                }
              >
                <ColourPicker itemType={item.itemType} itemNo={item.itemNo} existing={variants.map((v) => v.colorId)} selected={picked} onChange={setPicked} />
                <div className="mt-3">
                  <ErrorMessage error={addColours.error} />
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}

function VariantLine({
  v,
  itemNo,
  locations,
  busy,
  onPatch,
}: {
  v: VariantRow;
  itemNo: string;
  locations: { id: number; code: string }[];
  busy: boolean;
  onPatch: (p: Record<string, unknown>) => void;
}) {
  const [notes, setNotes] = useState(v.notes ?? '');
  return (
    <tr className={cn(!v.active && 'bg-slate-50 text-slate-400')}>
      <td className="whitespace-nowrap">
        <ColorSwatch rgb={v.colorRgb} name={v.colorName} /> {!v.active && <Badge>Retired</Badge>}
      </td>
      {(['N', 'U'] as const).map((cond) => {
        const stock = cond === 'N' ? v.onHandNew : v.onHandUsed;
        const coming = cond === 'N' ? v.onOrderNew : v.onOrderUsed;
        return (
          <Fragment key={cond}>
            <td className="border-l border-slate-100 text-right tabular-nums">
              {stock > 0 ? (
                <Link to={`/items?view=lots&itemNo=${encodeURIComponent(itemNo)}&colorId=${v.colorId}&condition=${cond}`} className="hover:underline" title="See the lot">
                  {number(stock)}
                </Link>
              ) : (
                <span className="text-slate-400">0</span>
              )}
            </td>
            <td className="text-right tabular-nums">{coming > 0 ? <Badge tone="blue">{number(coming)} coming</Badge> : <span className="text-slate-400">0</span>}</td>
          </Fragment>
        );
      })}
      <td className="border-l border-slate-100 text-right font-medium tabular-nums">{number(v.onHand)}</td>
      <td className="text-right font-medium tabular-nums">{number(v.onOrder)}</td>
      {(['N', 'U'] as const).map((cond) => {
        const mine = cond === 'N' ? v.defaultLocationNewId : v.defaultLocationUsedId;
        const other = cond === 'N' ? v.defaultLocationUsedId : v.defaultLocationNewId;
        const word = cond === 'N' ? 'new' : 'used';
        return (
          <td key={cond} className={cond === 'N' ? 'border-l border-slate-100' : undefined}>
            <Select
              aria-label={`Usual bin for ${word} ${v.colorName}`}
              className="w-28"
              disabled={busy}
              value={mine ?? ''}
              onChange={(e) => onPatch({ [cond === 'N' ? 'defaultLocationNewId' : 'defaultLocationUsedId']: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">—</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id} disabled={l.id === other}>
                  {l.code}
                </option>
              ))}
            </Select>
          </td>
        );
      })}
      <td>
        <Input
          aria-label={`Notes for ${v.colorName}`}
          className="min-w-32"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => notes.trim() !== (v.notes ?? '') && onPatch({ notes: notes.trim() || null })}
        />
      </td>
      <td className="text-right whitespace-nowrap">
        {v.active ? (
          <Button
            small
            variant="ghost"
            disabled={busy || v.onHand > 0 || v.onOrder > 0}
            title={v.onHand > 0 || v.onOrder > 0 ? 'It still has stock or is on order' : 'Hide it from the pickers. Its history stays.'}
            onClick={() => onPatch({ active: false })}
          >
            Retire
          </Button>
        ) : (
          <Button small variant="secondary" disabled={busy} onClick={() => onPatch({ active: true })}>
            Restore
          </Button>
        )}
      </td>
    </tr>
  );
}

function EditItem({ item, onDone }: { item: ItemDetailData['item']; onDone: () => void }) {
  const categories = useCategories();
  const [name, setName] = useState(item.name);
  const [categoryText, setCategoryText] = useState(item.categoryName ?? '');
  const [weight, setWeight] = useState(item.weightG ? String(Number(item.weightG)) : '');
  const [year, setYear] = useState(item.yearReleased ? String(item.yearReleased) : '');
  const category = categories.data?.find((c) => c.name.toLowerCase() === categoryText.trim().toLowerCase()) ?? null;
  // What BrickLink gave is not edited here; use Refresh from BrickLink on the Details tab if it has changed there.
  const fromBl = !!item.detailsFetchedAt && item.bricklinkKnown === true;
  const lock = fromBl ? { readOnly: true, className: 'bg-slate-100' } : {};
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/items/${item.id}`, {
        method: 'PATCH',
        body: {
          name: name.trim(),
          categoryId: categoryText.trim() ? (category?.id ?? item.categoryId) : null,
          weightG: weight.trim() ? Number(weight) : null,
          yearReleased: year.trim() ? Number(year) : null,
        },
      }),
    onSuccess: onDone,
  });
  return (
    <Card title="Edit item">
      <form
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" className="sm:col-span-2">
          <Input required {...lock} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Category">
          <Input list="edit-item-categories" {...lock} value={categoryText} onChange={(e) => setCategoryText(e.target.value)} />
          <datalist id="edit-item-categories">
            {categories.data?.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Weight (g)">
            <Input inputMode="decimal" {...lock} value={weight} onChange={(e) => setWeight(e.target.value)} />
          </Field>
          <Field label="Year">
            <Input inputMode="numeric" {...lock} value={year} onChange={(e) => setYear(e.target.value)} />
          </Field>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Button type="submit" disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
      <div className="mt-3">
        <ErrorMessage error={save.error} />
      </div>
    </Card>
  );
}


function IncomingTab({ incoming }: { incoming: ItemDetailData['incoming'] }) {
  if (incoming.length === 0) return <Card title="Incoming"><p className="text-sm text-slate-500">Nothing is currently on order for this item.</p></Card>;
  return <Card title={`Incoming (${incoming.length})`}><p className="mb-3 text-sm text-slate-500">Ordered quantities are not inventory until they are received. They become lots when the purchase is receipted.</p><div className="overflow-x-auto"><table className="data w-full min-w-[44rem] text-sm"><thead><tr><th>Purchase order</th><th>Supplier</th><th>Colour</th><th>Condition</th><th>Status</th><th className="text-right">Ordered</th><th className="text-right">Received</th><th className="text-right">Remaining</th></tr></thead><tbody>{incoming.map((line) => <tr key={`${line.purchaseId}-${line.colorId}-${line.condition}`}><td><Link className="font-medium hover:underline" to={`/purchases/${line.purchaseId}`}>{line.purchaseDescription || line.externalId || `Purchase ${line.purchaseId}`}</Link></td><td>{line.sellerName ?? '—'}</td><td>{line.colorName}</td><td>{line.condition === 'N' ? 'New' : 'Used'}</td><td className="capitalize">{line.status.replace('_', ' ')}</td><td className="text-right tabular-nums">{number(line.qtyOrdered)}</td><td className="text-right tabular-nums">{number(line.qtyReceived)}</td><td className="text-right font-medium tabular-nums">{number(line.outstanding)}</td></tr>)}</tbody></table></div></Card>;
}
